import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Edit from '@/images/Edit.svg';
import Upgrade from '@/images/FlashLightning.svg';
import styled from '@emotion/styled';
import { PopoverPosition, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslateProofreadAccess } from '../../hooks/useTranslateProofreadAccess';
import ProofreadUpgradePopup from './ProofreadUpgradePopup';

const StyledButton = styled(Button, { shouldForwardProp: p => p !== 'blocked' })<{ blocked: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 4.8rem;
    width: 100%;
    gap: 0.4rem;
    border-color: ${({ theme }) => theme.colors.black[300]};

    &.MuiButton-outlined:disabled {
        border-color: ${({ theme }) => theme.colors.black[300]} !important;
    }

    &:hover {
        border-color: ${({ theme }) => theme.colors.black[300]};
        cursor: ${({ blocked }) => (blocked ? 'not-allowed' : 'pointer')};
        ${({ blocked }) => blocked && `background-color: transparent;`}
    }
`;

const ButtonText = styled(Typography)`
    font-weight: 600;
    font-size: 1.8rem;
`;

const UpgradeIconContainer = styled.span`
    background-color: ${({ theme }) => theme.colors.primary.bgLight};
    height: 21px;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
`;

interface Props {
    disabled?: boolean;
    onClick?: () => void;
    isLoading?: boolean;
}

const DEFAULT_ANCHOR_POSITION = { top: 0, left: 0 };

export const ProofreadButton = ({ disabled, onClick, isLoading }: Props) => {
    const { proofreadVisible, proofreadEnabled } = useTranslateProofreadAccess();
    const [anchorPosition, setAnchorPosition] = useState<PopoverPosition>(DEFAULT_ANCHOR_POSITION);
    const [mouseOnButton, setMouseOnButton] = useState(false);
    const [mouseOnPopup, setMouseOnPopup] = useState(false);
    const theme = useTheme();

    function handleClick() {
        if (!proofreadEnabled) {
            return;
        }
        onClick?.();
    }

    function handleMouseEnter(e: React.MouseEvent<HTMLButtonElement>) {
        const { top } = e.currentTarget.getBoundingClientRect();
        const left = e.clientX;

        setAnchorPosition({ top: top - 5, left });
        setMouseOnButton(true);
    }

    function handleMouseLeave() {
        setMouseOnButton(false);
    }

    function handlePopupMouseEnter() {
        setMouseOnPopup(true);
    }

    function handlePopupMouseLeave() {
        setMouseOnPopup(false);
    }

    if (!proofreadVisible) {
        return null;
    }

    return (
        <>
            <StyledButton
                data-testid="translation-modal__proofread-button"
                variant="outlined"
                color="secondary"
                disabled={disabled}
                blocked={!proofreadEnabled}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                isLoading={isLoading}>
                <MaskedIcon src={Edit} size="24px" />
                <ButtonText>Proofread</ButtonText>
                {!proofreadEnabled && (
                    <UpgradeIconContainer>
                        <MaskedIcon src={Upgrade} size="16px" color={theme.colors.primary.main} />
                    </UpgradeIconContainer>
                )}
            </StyledButton>
            <ProofreadUpgradePopup
                open={!proofreadEnabled && (mouseOnButton || mouseOnPopup)}
                anchorPosition={anchorPosition}
                onMouseEnter={handlePopupMouseEnter}
                onMouseLeave={handlePopupMouseLeave}
            />
        </>
    );
};
